/**
 * BonusOffersRail is a rail component for displaying bonus offers.
 *
 * @module views/components/BonusOffersRail
 * @memberof -Common
 */

import './BonusOffersRail.scss';

import React from 'react';

import PropTypes from 'prop-types';

import { Rail, Slide } from '@ulta/core/components/Rail/Rail';

import MediumDealCard from '@ulta/modules/MediumDealCard/MediumDealCard';

import carouselUtils from '@ulta/utils/carouselUtils/carouselUtils';

/**
 * Represents a BonusOffersRail component
 *
 * @method
 * @param {BonusOffersRailProps} props - React properties passed from composition
 * @returns {React.ReactElement} - BonusOffersRail
 */
export const BonusOffersRail = ( props ) => {
  const {
    componentKey,
    carouselAccessibility,
    carouselCardAccessibility,
    carouselDetailsAccessibility,
    countLabel = defaultProps.countLabel,
    headerTitle,
    invokeMutation,
    nextAccessibility = defaultProps.nextAccessibility,
    nextClickAction,
    offers,
    previousAccessibility = defaultProps.previousAccessibility,
    previousClickAction,
    viewAllOffersAction
  } = props;

  const title = headerTitle.text;

  const railMeta = {
    countLabel,
    nextAccessibility,
    previousAccessibility
  };
  /* Interpolating accesibility strings */
  const newCarouselAccessibility = carouselUtils.handleTokenizedString( carouselAccessibility, [title] );
  const newCarouselCardAccessibility = carouselUtils.handleTokenizedString( carouselCardAccessibility, [title] );
  const newCarouselDetailsAccessibility = carouselUtils.handleTokenizedString( carouselDetailsAccessibility, [title] );

  /* ADA Objects */
  const railADAmeta = {
    carouselAccessibility: newCarouselAccessibility,
    carouselCardAccessibility: newCarouselCardAccessibility,
    carouselDetailsAccessibility: newCarouselDetailsAccessibility
  };


  return (
    <div className='BonusOffersRail'>
      <Rail
        items={ offers }
        headline={ title }
        headlineTag={ headerTitle.htmlTag }
        previousClickAction={ previousClickAction }
        nextClickAction={ nextClickAction }
        viewAllAction={ viewAllOffersAction }
        showBullets={ true }
        sliderPerViewXL={ 2.25 }
        slidesPerGroupXL={ 2 }
        sliderPerViewLG={ 2.75 }
        slidesPerGroupLG={ 2 }
        sliderPerViewMD={ 2.35 }
        slidesPerGroupMD={ 2 }
        sliderPerViewSM={ 1.10 }
        slidesPerGroupSM={ 1 }
        _railADAmeta={ railADAmeta }
        _railMeta={ railMeta }
      >
        { offers?.map( ( offer, cardsIndex ) => (
          <Slide key={ `BonusOffersRail-${ componentKey }-${ cardsIndex }` }>
            <MediumDealCard
              key={ cardsIndex }
              action1={ offer.actionGroup.action1 }
              action2={ offer.actionGroup.action2 }
              action1Style={ offer.actionGroup.action1Style }
              action2Style={ offer.actionGroup.action2Style }
              eyebrow={ offer.eyebrow }
              headline={ offer.headline }
              image={ offer.image }
              isOfferActivated={ offer.isOfferActivated }
              tag={ offer.tag }
              urgencyTag={ offer.urgencyTag }
              subHeadline={ offer.description }
              activateAction={ offer.activateAction }
              invokeMutation={ invokeMutation }
              offerId={ offer.offerId }
            />
          </Slide>
        ) ) }
      </Rail>
    </div>
  );
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {string} previousAccessibility='' - The default value of the previousAccessibility is empty string.
 * @property {string} nextAccessibility='' - The default value of the nextAccessibility is empty string.
 * @property {string} countLabel='' - The default value of the count label is empty string.
 */
export const defaultProps = {
  previousAccessibility: '',
  nextAccessibility: '',
  countLabel: ''
};

/**
 * @typedef BonusOffersRailProps
 * @type {object}

 * @property {string} carouselDetailsAccessibility - Value for aria label for carousel
 * @property {string} carouselAccessibility - Value for aria label for offer rail
 * @property {string} carouselCardAccessibility - Value for the offer card aria label
 * @property {Array<object>} offers - An array of offer objects to display in the rail.
 * @property {object} headerTitle - Object containing the text and HTML tag for the rail's title.
 * @property {Function} [previousClickAction] - Function to handle the previous button click.
 * @property {string} previousAccessibility - Sets the aria label for previous button.
 * @property {string} nextAccessibility - Sets the aria label for next button.
 * @property {Function} [nextClickAction] - Function to handle the next button click.
 * @property {object} [viewAllOffersAction] - Action object for the "View All" link.
 * @property {Function} [invokeMutation] - Function to call for activating an offer.
 */
export const propTypes = {
  carouselDetailsAccessibility: PropTypes.string,
  carouselAccessibility: PropTypes.string,
  carouselCardAccessibility: PropTypes.string,
  componentKey: PropTypes.string,
  offers: PropTypes.array.isRequired,
  headerTitle: PropTypes.object,
  previousClickAction: PropTypes.func,
  nextClickAction: PropTypes.func,
  previousAccessibility: PropTypes.string,
  nextAccessibility: PropTypes.string,
  viewAllOffersAction: PropTypes.object,
  invokeMutation: PropTypes.func
};

BonusOffersRail.propTypes = propTypes;
BonusOffersRail.defaultProps = defaultProps;

export default BonusOffersRail;
